/* ============================================================
   The whole signed-out flow — the web twin of app/src/screens/SignInScreen.tsx,
   step for step: sign in, create account, verify email, start a password reset,
   finish it. The copy and the branching are the mobile screen's; only the
   controls are HTML. See that file for why each sentence is worded as it is
   (the reset code is a conditional, the social hint is unconditional, ...).

   The page is the first thing anyone sees of the desktop, so it is a scene:
   the sky at this hour on the left with the sun rising as it loads, the form
   on the right. On a narrow window the sky becomes a band above the form.
   ============================================================ */
import React, { useCallback, useEffect, useState } from 'react';
import {
  CognitoError,
  emailIsValid,
  PASSWORD_RULES,
  passwordIsValid,
  type SocialProvider,
  useAuth,
} from '@habishine/client-core';

import { screen, track } from '../analytics';
import { Sky } from '../components/Sky';
import { SunMark } from '../components/SunMark';
import { Button, Field } from '../components/ui';
import { socialProviders } from '../config';
import { useNarrow } from '../shell/useNarrow';
import { useOsThemeWhileSignedOut } from '../theme/ThemeProvider';

type Step = 'signin' | 'signup' | 'verify' | 'forgot' | 'reset';

const PROVIDER_NAME: Record<SocialProvider, string> = {
  apple: 'Apple',
  google: 'Google',
  facebook: 'Facebook',
};

function joinProviders(providers: SocialProvider[]): string {
  const names = providers.map((p) => PROVIDER_NAME[p]);
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

/* implementation intentions, the Tiny Habits recipe — the kind of sentence
   the app is for, rotating slowly under the headline */
const RECIPES = [
  'After I pour my coffee, I will read one page.',
  'After I close my laptop, I will stretch for two minutes.',
  'After I brush my teeth, I will write one line in my journal.',
  'After I sit down at my desk, I will drink a glass of water.',
  'After lunch, I will walk around the block.',
];

export function SignInPage() {
  useOsThemeWhileSignedOut();
  const auth = useAuth();
  const [step, setStep] = useState<Step>('signin');
  // the same names the phone reports (auth_signin, auth_signup, auth_verify…)
  useEffect(() => screen(`auth_${step}`), [step]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [socialBusy, setSocialBusy] = useState<SocialProvider | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [destination, setDestination] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const error = localError ?? auth.error;

  const go = useCallback(
    (next: Step) => {
      setLocalError(null);
      auth.clearError();
      setCode('');
      setStep(next);
    },
    [auth],
  );

  const edit = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (localError) setLocalError(null);
    if (auth.error) auth.clearError();
    if (notice) setNotice(null);
    setter(e.target.value);
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch {
      // banner copy is set by AuthContext
    } finally {
      setBusy(false);
    }
  };

  const submitSignIn = () =>
    run(async () => {
      if (!emailIsValid(email)) return setLocalError('That doesn’t look like an email address.');
      if (!password) return setLocalError('Enter your password.');
      try {
        await auth.signInWithPassword(email, password);
      } catch (e) {
        if (e instanceof CognitoError && e.code === 'UserNotConfirmedException') {
          const dest = await auth.resendConfirmationCode(email).catch(() => null);
          setDestination(dest);
          go('verify');
        }
      }
    });

  const submitSignUp = () =>
    run(async () => {
      if (!emailIsValid(email)) return setLocalError('That doesn’t look like an email address.');
      if (!passwordIsValid(password)) {
        return setLocalError('Your password needs everything in the list below.');
      }
      const res = await auth.signUp(email, password);
      if (res.userConfirmed) {
        track('sign_up', { method: 'email' });
        await auth.signInWithPassword(email, password);
        return;
      }
      setDestination(res.destination);
      go('verify');
    });

  const submitVerify = () =>
    run(async () => {
      if (code.length !== 6) return setLocalError('Enter all six digits.');
      await auth.confirmSignUp(email, code);
      track('sign_up', { method: 'email' });
      if (password) {
        await auth.signInWithPassword(email, password);
        return;
      }
      setNotice('Email verified — you can sign in now.');
      go('signin');
    });

  const resend = () =>
    run(async () => {
      const dest = await auth.resendConfirmationCode(email);
      setDestination(dest);
      setNotice('New code sent.');
    });

  const submitForgot = () =>
    run(async () => {
      if (!emailIsValid(email)) return setLocalError('That doesn’t look like an email address.');
      const dest = await auth.forgotPassword(email);
      setDestination(dest);
      setNotice(null);
      go('reset');
    });

  const resendReset = () =>
    run(async () => {
      const dest = await auth.forgotPassword(email);
      setDestination(dest);
      setNotice('If that address has a password, a new code is on its way.');
    });

  const submitReset = () =>
    run(async () => {
      if (code.length !== 6) return setLocalError('Enter all six digits.');
      if (!passwordIsValid(newPassword)) {
        return setLocalError('Your new password needs everything in the list below.');
      }
      await auth.confirmForgotPassword(email, code, newPassword);
      setPassword('');
      setNewPassword('');
      setNotice('Password changed — sign in with your new one.');
      go('signin');
    });

  const onSocial = (p: SocialProvider) => {
    setLocalError(null);
    setSocialBusy(p);
    void auth.signInWithProvider(p).finally(() => setSocialBusy(null));
  };

  const anyBusy = busy || socialBusy !== null;
  const sentTo = destination ? `We sent a 6-digit code to ${destination}.` : 'We sent you a 6-digit code.';
  const resetSentTo = destination
    ? `If ${destination} has a password, a 6-digit code is on its way.`
    : 'If that address has a password, a 6-digit code is on its way.';
  const socialHint =
    socialProviders.length > 0
      ? `Signed up with ${joinProviders(socialProviders)}? Those accounts don't have a password — go back and use that button instead.`
      : null;

  const submit = (fn: () => void) => (e: React.FormEvent) => {
    e.preventDefault();
    fn();
  };

  const emailField = (
    <Field label="Email" type="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={edit(setEmail)} autoFocus />
  );
  const codeField = (
    <Field label="Code" inputMode="numeric" autoComplete="one-time-code" placeholder="6 digits" maxLength={6} value={code} onChange={edit(setCode)} autoFocus className="[&_input]:font-display [&_input]:text-[26px] [&_input]:tracking-[0.3em]" />
  );

  let body: React.ReactNode;
  switch (step) {
    case 'signup':
      body = (
        <Shell title="Create your account" sub="One place for the habits you're building and the things you have to do." onBack={() => go('signin')}>
          <form onSubmit={submit(submitSignUp)} className="space-y-3.5">
            <Banner error={error} notice={notice} />
            {emailField}
            <Field label="Password" type="password" autoComplete="new-password" placeholder="Choose a password" value={password} onChange={edit(setPassword)} />
            <Rules password={password} />
            <Button type="submit" variant="primary" className="w-full" busy={busy} disabled={anyBusy}>Create account</Button>
            <Button type="button" variant="ghost" className="w-full" onClick={() => go('signin')}>Already have an account? Sign in</Button>
          </form>
          <p className="mt-5 text-center text-xs text-tertiary">By creating an account you agree to our Terms and Privacy Policy.</p>
        </Shell>
      );
      break;
    case 'verify':
      body = (
        <Shell title="Check your email" sub={sentTo} onBack={() => go('signin')}>
          <form onSubmit={submit(submitVerify)} className="space-y-3.5">
            <Banner error={error} notice={notice} />
            {codeField}
            <Button type="submit" variant="primary" className="w-full" busy={busy} disabled={anyBusy || code.length !== 6}>Verify email</Button>
            <Button type="button" variant="ghost" className="w-full" onClick={resend} disabled={anyBusy}>Send a new code</Button>
          </form>
        </Shell>
      );
      break;
    case 'forgot':
      body = (
        <Shell title="Reset your password" sub="Tell us your email and we'll send a code to set a new one." onBack={() => go('signin')}>
          <form onSubmit={submit(submitForgot)} className="space-y-3.5">
            <Banner error={error} notice={notice} />
            {emailField}
            <Button type="submit" variant="primary" className="w-full" busy={busy} disabled={anyBusy}>Send code</Button>
          </form>
          {socialHint ? <p className="mt-5 text-xs leading-relaxed text-tertiary">{socialHint}</p> : null}
        </Shell>
      );
      break;
    case 'reset':
      body = (
        <Shell title="Set a new password" sub={resetSentTo} onBack={() => go('signin')}>
          <form onSubmit={submit(submitReset)} className="space-y-3.5">
            <Banner error={error} notice={notice} />
            {codeField}
            <Field label="New password" type="password" autoComplete="new-password" placeholder="Choose a new password" value={newPassword} onChange={edit(setNewPassword)} />
            <Rules password={newPassword} />
            <Button type="submit" variant="primary" className="w-full" busy={busy} disabled={anyBusy}>Change password</Button>
            <Button type="button" variant="ghost" className="w-full" onClick={resendReset} disabled={anyBusy}>Send a new code</Button>
          </form>
          {socialHint ? <p className="mt-5 text-xs leading-relaxed text-tertiary">{socialHint}</p> : null}
        </Shell>
      );
      break;
    default:
      body = (
        <Shell title="Welcome back" sub="Sign in to pick up where your day left off.">
          <form onSubmit={submit(submitSignIn)} className="space-y-3.5">
            <Banner error={error} notice={notice} />
            {emailField}
            <Field label="Password" type="password" autoComplete="current-password" placeholder="Your password" value={password} onChange={edit(setPassword)} />
            <div className="-mt-1 text-right">
              <button type="button" className="text-[13px] font-semibold text-frog hover:underline" onClick={() => go('forgot')}>Forgot password?</button>
            </div>
            <Button type="submit" variant="primary" className="w-full" busy={busy} disabled={anyBusy}>Sign in</Button>
          </form>
          {socialProviders.length > 0 ? (
            <div className="mt-5 space-y-2">
              <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-[1px] text-tertiary"><span className="h-px flex-1 bg-hairline" />or continue with<span className="h-px flex-1 bg-hairline" /></div>
              {socialProviders.map((p) => (
                <Button key={p} type="button" className="w-full" onClick={() => onSocial(p)} busy={socialBusy === p} disabled={busy || (socialBusy !== null && socialBusy !== p)}>
                  Continue with {PROVIDER_NAME[p]}
                </Button>
              ))}
            </div>
          ) : null}
          <Button type="button" variant="ghost" className="mt-5 w-full" onClick={() => go('signup')}>New here? Create an account</Button>
        </Shell>
      );
  }

  return (
    <div className="grain relative grid h-full grid-rows-[200px_minmax(0,1fr)] lg:grid-cols-[minmax(0,52%)_minmax(0,48%)] lg:grid-rows-[minmax(0,1fr)]">
      <Scene />
      <div className="relative z-[1] flex min-h-0 items-start justify-center overflow-y-auto px-5 py-8 lg:items-center lg:px-6 lg:py-10">
        <div className="screen-in w-full max-w-[380px]">{body}</div>
      </div>
    </div>
  );
}

/* The left half: the sky at this hour, the sun rising as the page mounts,
   the lockup, and a line that says what the app is for. */
function Scene() {
  const [i, setI] = useState(0);
  // the band above the form on a phone is short, so the sun sits lower there
  const narrow = useNarrow();
  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % RECIPES.length), 5200);
    return () => clearInterval(t);
  }, []);
  return (
    <Sky variant="scene" progress={narrow ? 0.45 : 0.62} className="h-full">
      <div className="flex h-full flex-col justify-between p-8 lg:p-12">
        <div className="flex items-center gap-3">
          <SunMark size={34} />
          <span className="wordmark text-[24px] text-[var(--sky-ink)]">habishine</span>
        </div>
        <div className="hidden lg:block">
          <h2 className="font-display max-w-[11ch] text-[56px] font-semibold leading-[1.02] text-[var(--sky-ink)]">Small reps. Bright days.</h2>
          <p key={i} className="fade-in mt-5 max-w-[40ch] text-[16px] leading-[1.5] text-[var(--sky-ink-2)]">
            <span className="font-display text-[20px] font-medium tracking-[-0.01em] text-[var(--sky-ink)]">“{RECIPES[i]}”</span>
            <br />
            Habits anchored to your day, tasks that stay out of the way, and a sun that rises as you go.
          </p>
        </div>
      </div>
    </Sky>
  );
}

function Shell({ title, sub, onBack, children }: { title: string; sub?: string; onBack?: () => void; children: React.ReactNode }) {
  return (
    <div>
      {onBack ? (
        <button type="button" onClick={onBack} className="mb-4 text-[13px] font-semibold text-frog hover:underline">← Back</button>
      ) : null}
      <h1 className="font-display text-[32px] font-semibold leading-[1.05] text-ink md:text-[38px]">{title}</h1>
      {sub ? <p className="mb-7 mt-2.5 text-[14.5px] leading-[1.5] text-ink-2">{sub}</p> : <div className="mb-7" />}
      {children}
    </div>
  );
}

function Banner({ error, notice }: { error: string | null; notice: string | null }) {
  if (error) return <p role="alert" className="fade-in rounded-tile bg-nudge px-3.5 py-2.5 text-sm text-ink">{error}</p>;
  if (notice) return <p className="fade-in rounded-tile bg-frog-soft px-3.5 py-2.5 text-sm text-ink">{notice}</p>;
  return null;
}

function Rules({ password }: { password: string }) {
  return (
    <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
      {PASSWORD_RULES.map((r) => {
        const ok = r.test(password);
        return (
          <li key={r.label} className={`flex items-center gap-1.5 transition-colors ${ok ? 'text-success' : 'text-tertiary'}`}>
            <span className={`flex h-3.5 w-3.5 items-center justify-center rounded-full text-[9px] ${ok ? 'bg-success text-white' : 'border border-hairline'}`}>{ok ? '✓' : ''}</span> {r.label}
          </li>
        );
      })}
    </ul>
  );
}
